import type { BuilderPublicPackagesUrls } from '@wix/thunderbolt-symbols'
import type { IBuilderComponentSdkLoader } from '../../types'

// Extend the worker global scope to include es-module-shims methods
declare const self: DedicatedWorkerGlobalScope & {
	importShim: {
		addImportMap: (map: { imports: Record<string, string> }) => void
		(url: string): Promise<any>
	}
}

let esModuleShimsInitialized = false

/**
 * Initializes es-module-shims in the worker environment.
 * This enables ES module import map support for resolving external dependencies.
 * Dependency hierarchy:
 * - @wix/builder-state: No dependencies
 * - @wix/builder-velo: Depends on @wix/builder-state
 * - @wix/builder-wix-components: Depends on both @wix/builder-velo and @wix/builder-state
 * - SDK itself: Depends on all three
 */
const initializeEsModuleShims = async (builderPublicPackagesUrls: BuilderPublicPackagesUrls, builderComponentsImportMapSdkUrls: Record<string, string>, shouldDisableBuilderWixComponents: boolean): Promise<void> => {
	if (esModuleShimsInitialized) {
		return
	}

	const importMap = {
		'@wix/builder-state': builderPublicPackagesUrls.esm['builder-state'],
		'@wix/builder-velo': builderPublicPackagesUrls.esm['builder-velo'],
		...(!shouldDisableBuilderWixComponents && {
			'@wix/builder-wix-components': builderPublicPackagesUrls.esm['builder-wix-components-sdks'],
		}),
		...builderComponentsImportMapSdkUrls
	}
	try {
		// Load es-module-shims polyfill for import maps in workers
		// Using the latest stable version from jspm.io CDN
		const esModuleShimsUrl = 'https://ga.jspm.io/npm:es-module-shims@1.8.2/dist/es-module-shims.js'

		// Import the es-module-shims library into the worker
		importScripts(esModuleShimsUrl)

		// Set up the import map for resolving builder component dependencies
		self.importShim.addImportMap({
			imports: importMap,
		})

		esModuleShimsInitialized = true
		console.log('es-module-shims initialized successfully with import map:', importMap)
	} catch (error) {
		console.error('Failed to initialize es-module-shims:', error)
		esModuleShimsInitialized = false
		throw new Error(`ES module shims initialization failed: ${error.message}`)
	}
}

/**
 * Creates a client-side builder component SDK loader that works in web worker environments.
 * Uses es-module-shims to handle ESM dependencies through import maps.
 *
 * @returns IBuilderComponentSdkLoader implementation
 */
export const createClientBuilderComponentSdkLoader = (builderPublicPackagesUrls: BuilderPublicPackagesUrls, builderComponentsImportMapSdkUrls: Record<string, string>, shouldDisableBuilderWixComponents: boolean = false): IBuilderComponentSdkLoader => {
	return {
		loadSdk: async (url: string): Promise<any> => {
			try {
				// Initialize es-module-shims if not already done
				await initializeEsModuleShims(builderPublicPackagesUrls, builderComponentsImportMapSdkUrls, shouldDisableBuilderWixComponents)

				// Use importShim instead of dynamic import to resolve dependencies
				// This allows the import map to properly resolve external dependencies
				const module = await self.importShim(url)

				// Return the default export (standard for ESM modules)
				if (module.default) {
					return module.default
				}

				// Fallback: if no default export, return the entire module
				return module
			} catch (error) {
				const errorMessage = `Failed to load Builder SDK from ${url}: ${error.message}`
				console.error(errorMessage, error)

				// ESM with default export - falling back to import component with no dependencies (support not Piler Components)
				return import(/* webpackIgnore: true */ url).then((sdk) => sdk.default)
			}
		},
	}
}
